import { Stack } from "@certa/catalyst/layouts/Stack";
import { Popover } from "@certa/catalyst/components/Popover";
import type { TagSizes } from "@certa/catalyst/components/Tag";
import { Tag, TagVariants, TagColors } from "@certa/catalyst/components/Tag";
import { Ellipsis } from "@certa/catalyst/components/Typography";
import type { Badge, BadgeUIMode } from "@certa/types";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuItemSizes
} from "@certa/catalyst/components/Dropdown";
import { forwardRef, type ReactNode } from "react";
import { BadgeComponent } from "./BadgeComponent";
import { computeBadgeIconAndColor } from "./utils/computeBadgeIconAndColor";
import { BadgePopoverContent } from "./BadgePopoverContent";

type BadgesGroupProps = {
  badges: Badge[];
  size?: TagSizes;
  mode?: BadgeUIMode;
  canShowOverflowPopover?: boolean;
  ariaLabel: string;
};

export const BadgesGroup = (props: BadgesGroupProps) => {
  const {
    badges,
    size = "Medium",
    mode: modeOverride,
    canShowOverflowPopover = true,
    ariaLabel
  } = props;

  if (!badges || badges.length === 0) return null;

  const badgesToShow = canShowOverflowPopover ? badges.slice(0, 3) : badges;

  const badgesToShowInPopover =
    canShowOverflowPopover && badges.length > 3 ? badges.slice(3) : undefined;

  return (
    <Stack
      direction="horizontal"
      align="center"
      gap="8"
      role="group"
      aria-label={ariaLabel}
    >
      {badgesToShow.map(badge => (
        <BadgeComponent
          key={badge.tag}
          badge={badge}
          size={size}
          modeOverride={modeOverride}
        />
      ))}

      {badgesToShowInPopover && (
        <DropdownMenu>
          <DropdownMenuTrigger>
            <Tag
              variant={TagVariants.FILLED}
              size={size}
              type="Text"
              color={TagColors.NEUTRAL}
              label={`+${badges.length - 3} badges`}
            >
              {`+${badges.length - 3}`}
            </Tag>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <Stack padding="4">
              <DropdownMenuGroup>
                {badgesToShowInPopover.map(badge => {
                  const { icon } = computeBadgeIconAndColor(badge, {
                    modeOverride: "icon"
                  });

                  return (
                    <Popover
                      key={badge.tag}
                      openInPortal
                      content={<BadgePopoverContent badge={badge} />}
                      placement="left"
                      trigger="hover"
                      ariaDescription={badge.name}
                    >
                      <div>
                        <DropdownMenuItem
                          value={badge.name}
                          onSelect={() => {}}
                          size={DropdownMenuItemSizes.BIG}
                          isAsChildEnabled
                        >
                          <IconWithText icon={icon} text={badge.name} />
                        </DropdownMenuItem>
                      </div>
                    </Popover>
                  );
                })}
              </DropdownMenuGroup>
            </Stack>
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </Stack>
  );
};

const IconWithText = forwardRef<
  HTMLDivElement,
  {
    icon: ReactNode;
    text: string;
  }
>(({ icon, text, ...restProps }, ref) => {
  const {
    // @ts-expect-error - Need to remove 'onPointerLeave' property passed
    // down by DropdownMenuItem so that the popover doesn't close when moving
    // pointer outside this component (DropdownMenuItem children).
    //
    // Slack thread with explanation:
    // https://vetted.slack.com/archives/C0120UHB3DX/p1722490471144719?thread_ts=1721205260.833769&cid=C0120UHB3DX
    onPointerLeave,
    ...restProps2
  } = restProps;

  return (
    <div ref={ref} {...restProps2}>
      <Stack
        direction="horizontal"
        align="center"
        gap="8"
        overflow="hidden"
        maxWidth="12.5rem"
      >
        {icon}
        <Ellipsis>{text}</Ellipsis>
      </Stack>
    </div>
  );
});
